import React from 'react';
import Box from '@mui/material/Box';
import { INK, MONO } from './tokens';

const LINE_HEIGHT = 20;

export default function InputEditor({
  value,
  onChange,
  onSubmit,
  disabled,
  placeholder,
  accent,
  autoFocus = false,
  height = 168
}) {
  const gutterRef = React.useRef(null);
  const textRef = React.useRef(null);

  // The gutter is its own scroll box, so it has to follow the textarea.
  const syncScroll = () => {
    if (gutterRef.current && textRef.current) {
      gutterRef.current.scrollTop = textRef.current.scrollTop;
    }
  };

  const lineCount = Math.max(value.split('\n').length, 1);

  // Cmd/Ctrl-Enter submits, so Enter stays a newline.
  const handleKeyDown = (e) => {
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault();
      onSubmit?.();
    }
  };

  return (
    <Box
      sx={{
        borderRadius: 1.5,
        overflow: 'hidden',
        backgroundColor: INK.surface,
        border: '1px solid transparent',
        transition: 'border-color 120ms ease',
        '&:focus-within': { borderColor: accent },
        '@media (prefers-reduced-motion: reduce)': { transition: 'none' }
      }}
    >
      <Box sx={{ display: 'flex', height }}>
        <Box
          ref={gutterRef}
          aria-hidden="true"
          sx={{
            width: 30,
            flexShrink: 0,
            backgroundColor: INK.gutter,
            overflow: 'hidden',
            py: 1,
            fontFamily: MONO,
            fontSize: 11,
            lineHeight: `${LINE_HEIGHT}px`,
            color: INK.dim,
            textAlign: 'right',
            userSelect: 'none'
          }}
        >
          {Array.from({ length: lineCount }, (_, i) => (
            <div key={i} style={{ paddingRight: 6 }}>
              {i + 1}
            </div>
          ))}
        </Box>

        <Box
          component="textarea"
          ref={textRef}
          value={value}
          disabled={disabled}
          autoFocus={autoFocus}
          spellCheck={false}
          autoComplete="off"
          aria-label="Family description or pedigree data"
          placeholder={placeholder}
          onChange={(e) => onChange(e.target.value)}
          onScroll={syncScroll}
          onKeyDown={handleKeyDown}
          sx={{
            flex: 1,
            minWidth: 0,
            resize: 'none',
            border: 'none',
            outline: 'none',
            backgroundColor: 'transparent',
            color: INK.text,
            caretColor: accent,
            fontFamily: MONO,
            fontSize: 12.5,
            lineHeight: `${LINE_HEIGHT}px`,
            padding: '8px 10px 8px 4px',
            '&::placeholder': { color: INK.dim, opacity: 1 },
            '&:disabled': { opacity: 0.55 },
            '&::-webkit-scrollbar': { width: 8 },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: INK.line,
              borderRadius: 4
            }
          }}
        />
      </Box>
    </Box>
  );
}
